import { useCallback, useEffect, useMemo } from 'react'
import { UseQueryResult, UseInfiniteQueryResult, InfiniteData } from '@tanstack/react-query'
import { usePrevious } from '@commutifi-fe/hooks'
import { Select, AppSelectProps, LabeledValue, BaseOptionType, DefaultOptionType } from '../AppSelect'
import { ErrorTryAgain } from '../errors/ErrorTryAgain'
import { useLocaleReceiver } from '../locales/LocaleReceiver'

export enum FetchSelectType {
  MULTIPLE = 'multiple',
  SINGLE = 'single'
}

export type OmitBaseSelectProps = 'data' | 'error' | 'isLoading' | 'refetch'
export interface BaseFetchSelectProps<
  Type,
  Data,
  TPageParam,
  ValueType = string,
  OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType
> extends Omit<AppSelectProps<ValueType, OptionType>, 'loading'> {
  // Multiple on single select
  type?: `${FetchSelectType}`
  // Gives access to the api result (data of type any is the api response type)
  // to display the Select component Options using the allowed CHILDREN_TYPES above
  // This function is executed before displaying Select Options to format
  // them as we need.
  // NOTE: We can't specify with typescript but this function should return compound
  // components of this component: Option or OptGroup
  renderOptions: (
    res: (Type extends 'Infinite' ? InfiniteData<Data, TPageParam> : Data) | undefined
  ) => NonNullable<AppSelectProps<ValueType, OptionType>['options']>
  // Pass that function to control refetching the request
  shouldUpdate?: (previousValue: ValueType, nextValue: ValueType | null | undefined) => boolean
  refetch: (Type extends 'Infinite'
    ? UseInfiniteQueryResult<InfiniteData<Data, TPageParam>, Error>
    : UseQueryResult<Data, Error>)['refetch']
  data: (Type extends 'Infinite'
    ? UseInfiniteQueryResult<InfiniteData<Data, TPageParam>>
    : UseQueryResult<Data>)['data']
  error: (Type extends 'Infinite'
    ? UseInfiniteQueryResult<InfiniteData<Data, TPageParam>>
    : UseQueryResult<Data>)['error']
  isLoading: (Type extends 'Infinite'
    ? UseInfiniteQueryResult<InfiniteData<Data, TPageParam>>
    : UseQueryResult<Data>)['isLoading']
  isProcessing?: (Type extends 'Infinite'
    ? UseInfiniteQueryResult<InfiniteData<Data, TPageParam>>
    : UseQueryResult<Data>)['isFetching']
}

export function BaseFetchSelect<
  Type extends 'Infinite' | 'Default',
  Data,
  TPageParam,
  ValueType extends string | number | string[] | number[] | LabeledValue | LabeledValue[] = string,
  OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType
>({
  value,
  renderOptions,
  shouldUpdate,
  refetch,
  isLoading,
  isProcessing,
  data,
  error,
  type,
  ...rest
}: BaseFetchSelectProps<Type, Data, TPageParam, ValueType, OptionType>) {
  const intl = useLocaleReceiver()
  const previousValue = usePrevious(value) as ValueType

  const selectOptions = useMemo(() => renderOptions(data as any), [data, renderOptions])

  useEffect(() => {
    if (shouldUpdate && previousValue && shouldUpdate(previousValue, value)) {
      refetch()
    }
  }, [previousValue, refetch, shouldUpdate, value])

  const renderCommon = useCallback((): NonNullable<AppSelectProps<ValueType>['options']> => {
    const handleRetry = () => {
      refetch()
    }
    return isLoading
      ? [
          {
            key: 'app-fetch-select__loading',
            value: 'select-loading',
            label: intl.formatMessage({ id: 'global.loading' }),
            disabled: true
          }
        ]
      : error
        ? [
            {
              key: 'app-fetch-select__error',
              value: 'select-error',
              disabled: true,
              label: <ErrorTryAgain key="error" onClick={handleRetry} />
            }
          ]
        : data
          ? selectOptions
          : []
  }, [isLoading, intl, error, data, selectOptions, refetch])

  return (
    <Select<ValueType>
      loading={isProcessing}
      value={value}
      options={renderCommon()}
      {...(type === FetchSelectType.MULTIPLE && { mode: 'checkboxes' })}
      {...rest}
      // Avoid showing the checkboxes or any of the specific 'multiple' mode options to select more
      // than one when we have an error so we properly show the try again button
      {...(!!error && { mode: undefined })}
    />
  )
}
